import FormGroupRenderer from '@app/components/FormGroupRenderer'
import PageSection from '@app/components/PageSection'
import PillListRenderer from '@app/components/PillListRenderer'
import ShareModal from '@app/components/ShareModal'
import { ICriticalDateContact } from '@app/models/CriticalDateContact'
import { IShareContact } from '@app/models/ShareContact'
import { bindFunctions } from '@app/utils/class'
import {
  formatAddressSingleLine,
  formatCurrency,
  formatDate,
  humanize,
} from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { List } from 'immutable'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Alert, Button, Col, Label, Row, Table } from 'reactstrap'
import { DevelopmentSiteViewPageProps } from './DevelopmentSiteViewPage'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd'
import { IDueDiligenceItem } from '@app/models/DueDiligenceItem'
import CriticalDatesEditModalContainer from '@app/components/CriticalDatesEditModal/containers/CriticalDatesEditModalContainer'

export default class DevelopmentSiteCriticalDates extends React.PureComponent<DevelopmentSiteViewPageProps> {
  constructor(props: DevelopmentSiteViewPageProps) {
    super(props)
    bindFunctions(this)
  }

  public componentDidMount() {
    this.props.loadCriticalDatesDocument(this.props.site.id)
  }

  public componentDidUpdate(prevProps: DevelopmentSiteViewPageProps) {
    if (prevProps.site.id !== this.props.site.id) {
      this.props.loadCriticalDatesDocument(this.props.site.id)
    }
  }

  public render() {
    const renderBody = this.props.isLoading
      ? this._renderLoadingBody
      : this._renderCriticalDatesDocument

    return (
      <DocumentTitle
        title={`${this.props.site.client.name} :: ${formatAddressSingleLine(
          this.props.site.address,
        )} :: Critical Dates`}
      >
        {renderBody()}
      </DocumentTitle>
    )
  }

  private _onPrint() {
    window.print()
  }

  private _onShare() {
    this.props.showShareCriticalDates()
  }

  private _onShareConfirmed(contacts: List<IShareContact>, comment: string) {
    this.props.shareCriticalDates(this.props.site.id, contacts, comment)
  }

  private _renderCriticalDatesDocument() {
    if (!this.props.criticalDatesDoc) {
      return (
        <Alert color='danger'>
          <div>
            We are not able to load the critical dates document right now.
            Please try back later.
          </div>
          {this.props.error && <div>{this.props.error}</div>}
        </Alert>
      )
    }

    const actions = [
      <Button
        color='link'
        className='d-print-none'
        key='share'
        onClick={this._onShare}
        size='lg'
        title='Share'
      >
        <FontAwesomeIcon icon={['fal', 'share-alt']} fixedWidth />
      </Button>,
      <Button
        color='link'
        className='d-print-none'
        key='print'
        onClick={this._onPrint}
        size='lg'
        title='Print'
      >
        <FontAwesomeIcon icon={['fal', 'print']} fixedWidth />
      </Button>,
    ]

    return (
      <PageSection title='Critical Dates' actions={actions}>
        <FormGroupRenderer className='mb-0' labelText='Buyer'>
          {this._renderCriticalDatesContacts(
            this.props.criticalDatesDoc.buyers,
          )}
        </FormGroupRenderer>
        <FormGroupRenderer className='mb-3' labelText='Buyer Broker'>
          {this._renderCriticalDatesContacts(
            this.props.criticalDatesDoc.buyerBrokers,
          )}
        </FormGroupRenderer>
        <FormGroupRenderer className='mb-0' labelText='Seller'>
          {this._renderCriticalDatesContacts(
            this.props.criticalDatesDoc.sellers,
          )}
        </FormGroupRenderer>
        <FormGroupRenderer className='mb-3' labelText='Seller Broker'>
          {this._renderCriticalDatesContacts(
            this.props.criticalDatesDoc.sellerBrokers,
          )}
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Property'>
          {formatAddressSingleLine(this.props.criticalDatesDoc.address)}
        </FormGroupRenderer>
        <Row className='mb-3'>
          <Col className='col-pr-4' md={4} xl={3}>
            <Label className='mb-0'>Price</Label>
            <div>{formatCurrency(this.props.criticalDatesDoc.price, 0)}</div>
          </Col>
          <Col>
            <Label className='mb-0'>Contract Effective Date</Label>
            <div>
              {formatDate(
                this.props.criticalDatesDoc.effectiveDate,
                'MM/DD/YYYY',
                'TBD',
              )}
            </div>
          </Col>
        </Row>
        {this.props.criticalDatesDoc.earnestMoney && (
          <Row className='mb-3'>
            <Col xs='auto'>
              <Label className='mb-0'>Deposit</Label>
              {this.props.criticalDatesDoc.earnestMoney.earnestMoney && (
                <div>
                  {formatCurrency(
                    this.props.criticalDatesDoc.earnestMoney.earnestMoney
                      .amount,
                    0,
                  )}
                </div>
              )}
            </Col>
            <Col xs='auto'>
              <Label className='mb-0'>Due Date</Label>
              <div>
                {formatDate(
                  this.props.criticalDatesDoc.earnestMoney.dueDate,
                  'MM/DD/YYYY',
                  'TBD',
                )}
              </div>
            </Col>
            <Col xs='auto'>
              <Label className='mb-0'>Received</Label>
              {this.props.criticalDatesDoc.earnestMoney.earnestMoney && (
                <div>
                  {formatDate(
                    this.props.criticalDatesDoc.earnestMoney.earnestMoney
                      .depositDate,
                    'MM/DD/YYYY',
                  )}
                </div>
              )}
            </Col>
            <Col>
              <Label className='mb-0'>Held By</Label>
              {this.props.criticalDatesDoc.earnestMoney.earnestMoney && (
                <div>
                  {
                    this.props.criticalDatesDoc.earnestMoney.earnestMoney
                      .holdingLocation
                  }
                </div>
              )}
            </Col>
            <Col className='d-flex justify-content-end'>
              <CriticalDatesEditModalContainer
                criticalDatesDoc={this.props.criticalDatesDoc}
              />
            </Col>
          </Row>
        )}
        <Table size='sm' striped>
          <thead>
            <tr>
              <th />
              <th />
              <th>Critical Dates</th>
              <th>Responsibility</th>
              <th>Date</th>
              <th>Assigned To</th>
            </tr>
          </thead>
          <DragDropContext
            onDragEnd={(result: DropResult) => {
              if (!result.destination) {
                return
              }

              this.props.updateDueDiligenceItem(
                result.draggableId,
                this.props.site.id,
                result.destination.index,
              )
            }}
          >
            <Droppable droppableId='dates'>
              {(provided) => (
                <tbody {...provided.droppableProps} ref={provided.innerRef}>
                  {this.props
                    .criticalDatesDoc!.criticalDates.toArray()
                    .map((criticalDate: IDueDiligenceItem, index: number) => (
                      <Draggable
                        key={criticalDate.id}
                        draggableId={criticalDate.id}
                        index={index}
                      >
                        {(provided) => (
                          <tr
                            ref={provided.innerRef}
                            {...provided.dragHandleProps}
                            {...provided.draggableProps}
                          >
                            <td>
                              <FontAwesomeIcon
                                icon={['fas', 'grip-lines']}
                                fixedWidth
                              />
                            </td>
                            <td style={{ width: '24px' }}>
                              {criticalDate.isComplete && (
                                <FontAwesomeIcon
                                  className='text-primary'
                                  icon={['fas', 'check']}
                                  fixedWidth
                                />
                              )}
                            </td>
                            <td>{criticalDate.title}</td>
                            <td>{humanize(criticalDate.responsibility)}</td>
                            <td>
                              {formatDate(
                                criticalDate.dueDate,
                                'MM/DD/YYYY',
                                'TBD',
                              )}
                            </td>
                            <td>
                              <PillListRenderer
                                idValue='contactId'
                                list={criticalDate.assignedTo}
                                baseNavigateUrl='/connection'
                              />
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        </Table>
        <FormGroupRenderer labelText='Closing Date'>
          {formatDate(
            this.props.criticalDatesDoc.closingDate,
            'MM/DD/YYYY',
            'TBD',
          )}
        </FormGroupRenderer>
        <FormGroupRenderer className='mb-0' labelText='Title Company'>
          {this._renderCriticalDatesContacts(
            this.props.criticalDatesDoc.titleCompanies,
          )}
        </FormGroupRenderer>
        <FormGroupRenderer className='mb-3' labelText='Title Company Contacts'>
          {this._renderCriticalDatesContacts(
            this.props.criticalDatesDoc.titleCompanyContacts,
          )}
        </FormGroupRenderer>
        <ShareModal
          defaultContacts={this.props.site.shareContacts}
          isOpen={this.props.showShareCriticalDatesModal}
          isSaving={this.props.isSharingCriticalDates}
          onCancel={this.props.closeShareCriticalDates}
          onShare={this._onShareConfirmed}
          title='Share Critical Dates'
        />
      </PageSection>
    )
  }

  private _renderCriticalDatesContacts(contacts: List<ICriticalDateContact>) {
    return contacts.toArray().map((contact) => (
      <div key={contact.contactId}>
        {contact.name}
        {contact.emailAddress && (
          <a className='ml-1' href={`mailto:${contact.emailAddress}`}>
            <FontAwesomeIcon icon={['fal', 'envelope']} fixedWidth />
          </a>
        )}
      </div>
    ))
  }

  private _renderLoadingBody() {
    return <div className='text-center'>Retrieving critical dates document</div>
  }
}
